import { Range } from "@tiptap/core";
import { ResolvedPos } from "prosemirror-model";

export interface Trigger {
  char: string;
  allowSpaces: boolean;
  prefixSpace: boolean;
  startOfLine: boolean;
  $position: ResolvedPos;
}

export type SuggestionMatch = {
  range: Range;
  query: string;
  text: string;
} | null;

export function findSuggestionMatch(config: Trigger): SuggestionMatch {
  let { char, allowSpaces, prefixSpace, startOfLine, $position } = config;

  // Matching expressions used for later
  const escapedChar = char
    .split("")
    .map((c) => `\\${c}`)
    .join("");
  const suffix = new RegExp(`\\s${escapedChar}$`);
  const prefix = startOfLine ? "^" : "";
  //   const prefix = ' ';
  const regexp = allowSpaces
    ? new RegExp(`${prefix}${escapedChar}.*?(?=\\s|$)`, "gim")
    : new RegExp(`${prefix}(?:^)?${escapedChar}[^\\s]*`, "gim");

  const isTopLevelNode = $position.depth <= 0;
  const textFrom = isTopLevelNode ? 0 : $position.before();
  let textTo = $position.pos;
  let text = $position.doc.textBetween(textFrom, textTo, "\0", "\0");
  let match = Array.from(text.matchAll(regexp)).pop();

  let retry = false;

  if (!match || match.input === undefined || match.index === undefined) {
    retry = true;
    textTo = $position.pos + 1;
    text = $position.doc.textBetween(textFrom, textTo, "\0", "\0");
    match = Array.from(text.matchAll(regexp)).pop();

    if (!match || match.input === undefined || match.index === undefined) {
      return null;
    }
  }

  // JavaScript doesn't have lookbehinds. This hacks a check that first character
  // is a space or the start of the line
  const matchPrefix = match.input.slice(
    Math.max(0, match.index - 1),
    match.index
  );
  const matchPrefixIsSpace = /^[\s\0]?$/.test(matchPrefix);

  if (prefixSpace && !matchPrefixIsSpace) {
    return null;
  }

  // The absolute position of the match in the document
  const from = match.index + $position.start();
  let to = from + match[0].length;

  // Edge case handling; if spaces are allowed and we're directly in between
  // two triggers
  if (allowSpaces && suffix.test(text.slice(to - 1, to + 1))) {
    match[0] += " ";
    to += 1;
  }

  const pos = retry ? $position.pos + 1 : $position.pos;

  // If the $position is located within the matched substring, return that range
  if (from < pos && to >= pos) {
    return {
      range: {
        from,
        to,
      },
      query: match[0].slice(char.length),
      text: match[0],
    };
  }

  return null;
}
